<template>
  <div v-if="diary">
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="[
          { title: 'School Diary', href: '/school-diary', color: 'primary' },
          { title: dateLabel },
        ]"
      ></v-breadcrumbs>
    </div>
    <v-progress-linear
      v-if="isUpdating || loading"
      indeterminate
      color="primary"
      class="my-8"
    ></v-progress-linear>
    <div class="d-flex pa-4">
      <h2>{{ dateLabel }}</h2>
      <div class="ml-4">
        <span v-if="isApproved || isApprovedByChief"
          ><v-chip color="primary" dark>{{ $t('schoolDiary.approved') }}</v-chip></span
        >
        <span v-else-if="applicationStatus === 'applied'"
          ><v-chip color="primary" variant="outlined" dark>{{ $t('schoolDiary.applied') }}</v-chip></span
        >
        <span v-else><v-chip color="grey" variant="outlined" dark>{{ $t('schoolDiary.draft') }}</v-chip></span>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        v-if="!isApproved"
        color="grey"
        class="mr-4"
        variant="outlined"
        x-large
        @click="saveAsDraft"
        :disabled="isUpdating"
      >
        <div v-if="isDraft">{{ $t('schoolDiary.saveAsDraft') }}</div>
        <div v-else>{{ $t('schoolDiary.returnDraft') }}</div>
      </v-btn>
      <v-btn
        v-if="isDraft"
        color="primary"
        variant="flat"
        x-large
        @click="saveAndApply"
        :disabled="isUpdating || appliedBy === null"
        >{{ $t('schoolDiary.saveAndApply') }}</v-btn
      >
      <div v-if="(isDirector || isChief) && isApplied">
        <v-btn
          variant="flat"
          color="primary"
          x-large
          class="ml-2"
          :disabled="isUpdating || isApprovedByMe"
          @click="approve"
          >{{ $t('schoolDiary.approve') }}</v-btn
        >
      </div>
      <div
        v-if="
          (isChief && isApplied && isApprovedByChief) ||
          (isDirector && isApplied && isApproved)
        "
      >
        <v-btn
          variant="flat"
          color="error"
          large
          class="ml-2"
          :disabled="isUpdating"
          @click="reject"
          >{{ $t('schoolDiary.revokeApproval') }}</v-btn
        >
      </div>
    </div>

    <v-row>
      <v-col cols="6"> </v-col>
      <v-col cols="2">
        <v-text-field
          :label="$t('settings.director')"
          readonly
          variant="outlined"
          :model-value="directorApproveStamp"
        >
        </v-text-field>
      </v-col>
      <v-col cols="2">
        <v-text-field :label="$t('settings.chief')" readonly variant="outlined" :model-value="cheifApproveStamp">
        </v-text-field>
      </v-col>
      <v-col cols="2">
        <v-select
          variant="outlined"
          :label="$t('schoolDiary.applicant')"
          :items="staffOptions"
          item-value="userId"
          item-title="name"
          v-model="appliedBy"
        ></v-select>
      </v-col>
    </v-row>

    <div>
      <h2>{{ $t('schoolDiary.title') }}</h2>

      <div class="pt-6">
        <h3>{{ $t('schoolDiary.classDiaryList') }}</h3>
      </div>

      <v-card
        v-for="cd in diary.classDiaries"
        :key="cd.classDiaryId"
        variant="outlined"
        class="pa-4 mb-4"
      >
        <!-- {{ cd }} -->
        <v-row>
          <v-col cols="2">
            <div class="class-diary-label">{{ $t('common.class') }}</div>
            <div>
              {{ getClassName(cd.classId) }}
            </div>
          </v-col>
          <v-col cols="3">
            <div class="class-diary-label">{{ $t('schoolDiary.todaysAim') }}</div>
            <div class="class-diary-content">
              {{ cd.aim }}
            </div>
          </v-col>
          <v-col cols="3">
            <div class="class-diary-label">{{ $t('schoolDiary.mainActivityAM') }}</div>
            <div class="class-diary-content">
              {{ cd.activityAm }}
            </div>
          </v-col>
          <v-col cols="3">
            <div class="class-diary-label">{{ $t('schoolDiary.mainActivityPM') }}</div>
            <div class="class-diary-content">
              {{ cd.activityPm }}
            </div>
          </v-col>
        </v-row>
      </v-card>
    </div>

    <div class="my-8">
      <h2>{{ $t('schoolDiary.absentees') }}</h2>
      <v-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">{{ $t('schoolDiary.childName') }}</th>
              <th class="text-left">{{ $t('schoolDiary.className') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="child in diary.absentees" :key="child.childId">
              <td>{{ child.lastName }} {{ child.firstName }}</td>
              <td>{{ getCurrentClass(child) }}</td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>

    <v-row>
      <v-col cols="6">
        <h4>{{ $t('schoolDiary.freeNotes') }}</h4>
        <v-textarea
          :readonly="isApproved || isApplied"
          variant="outlined"
          v-model="comment"
        ></v-textarea>
      </v-col>
      <v-col cols="6">
        <h4>{{ $t('schoolDiary.schoolEvents') }}</h4>
        <v-textarea
          :readonly="isApproved || isApplied"
          variant="outlined"
          v-model="event"
        ></v-textarea>
      </v-col>
      <v-col cols="6">
        <h4>{{ $t('schoolDiary.earlyLeavers') }}</h4>
        <v-textarea
          :readonly="isApproved || isApplied"
          variant="outlined"
          v-model="earlyLeaver"
        ></v-textarea>
      </v-col>
    </v-row>

    <div>
      <v-progress-linear
        v-if="isUpdating"
        indeterminate
        color="primary"
        class="my-8"
      ></v-progress-linear>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import { schoolDairyEditModule } from "@/store/dataModules/schoolDiaryEditModule";
import { meModule } from "@/store/dataModules/meModule";
import LocalDataService from "@/service/LocalDataService";
import dayjs from "dayjs";
import { classModule } from "@/store/dataModules/classModule";
import {
  NurseryChildResponse,
  NurseryDiarySettingResponse,
} from "chaild-api/lib";
import { nurseryUserModule } from "@/store/dataModules/nurseryUserModule";
import { diarySettingModule } from "@/store/dataModules/diarySettingModule";
import CommonUtil from "@/utils/CommonUtil";

interface DataType {
  isUpdating: boolean;
  loading: boolean;
  alertSetting: {
    shouldAlert: boolean;
    classes: string[];
  };
}

export default defineComponent({
  computed: {
    diary() {
      return schoolDairyEditModule.diary;
    },
    classes() {
      return classModule.classes;
    },
    dateLabel(): string {
      if (this.diary) {
        return dayjs(this.diary.date).format("DD/MM/YYYY");
      }
      return "";
    },
    applicationStatus(): string {
      if (this.diary) {
        return this.diary.status;
      }
      return "";
    },
    comment: {
      get() {
        if (schoolDairyEditModule.diary) {
          return schoolDairyEditModule.diary.comment;
        }
        return null;
      },
      set(value: string) {
        if (schoolDairyEditModule.diary) {
          const newValue = { ...schoolDairyEditModule.diary };
          newValue.comment = value;
          schoolDairyEditModule.setDiary(newValue);
        }
      },
    },
    event: {
      get() {
        if (schoolDairyEditModule.diary) {
          return schoolDairyEditModule.diary.event;
        }
        return null;
      },
      set(value: string) {
        if (schoolDairyEditModule.diary) {
          const newValue = { ...schoolDairyEditModule.diary };
          newValue.event = value;
          schoolDairyEditModule.setDiary(newValue);
        }
      },
    },
    earlyLeaver: {
      get() {
        if (schoolDairyEditModule.diary) {
          return schoolDairyEditModule.diary.earlyLeaver;
        }
        return null;
      },
      set(value: string) {
        if (schoolDairyEditModule.diary) {
          const newValue = { ...schoolDairyEditModule.diary };
          newValue.earlyLeaver = value;
          schoolDairyEditModule.setDiary(newValue);
        }
      },
    },
    staffOptions() {
      return nurseryUserModule.staffs.filter(s => !s.isHidden).map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`,
        };
      });
    },
    me() {
      return meModule.me;
    },
    isDirector() {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (currentAccount && currentAccount.isDirector) {
          return true;
        }
      }
      return false;
    },
    isChief() {
      if (this.me) {
        const nurseryId = LocalDataService.getNurseryId();
        const currentAccount = this.me.nurserySchools.find(
          (s) => s.nurseryId === nurseryId
        );
        if (currentAccount && currentAccount.isChief) {
          return true;
        }
      }
      return false;
    },
    isApproved(): boolean {
      if (this.diary) {
        // return this.diary.approvedBy.director !== -1;
        return (
          this.diary.approvedBy.director != null &&
          this.diary.approvedBy.director != -1
        );
      }
      return false;
    },
    isApprovedByChief(): boolean {
      if (this.diary) {
        // return this.diary.approvedBy.chief !== -1;
        return (
          this.diary.approvedBy.chief != null &&
          this.diary.approvedBy.chief != -1
        );
      }
      return false;
    },
    isApprovedByMe(): boolean {
      if (this.diary && meModule.me) {
        if (
          this.diary.approvedBy &&
          this.diary.approvedBy.director &&
          this.diary.approvedBy.director != -1
        ) {
          const directorNuseryId = this.diary.approvedBy.director.nurseryId;
          const directorUserId = this.diary.approvedBy.director.userNurseryId;
          const findAccount = meModule.me.nurserySchools.find(
            (ac) => ac.nurseryId === directorNuseryId
          );
          if (findAccount && findAccount.userId === directorUserId) {
            return true;
          }
        }

        if (
          this.diary.approvedBy &&
          this.diary.approvedBy.chief &&
          this.diary.approvedBy.chief != -1
        ) {
          const chiefNuseryId = this.diary.approvedBy.chief.nurseryId;
          const chiefUserId = this.diary.approvedBy.chief.userNurseryId;
          const findAccount = meModule.me.nurserySchools.find(
            (ac) => ac.nurseryId === chiefNuseryId
          );
          if (findAccount && findAccount.userId === chiefUserId) {
            return true;
          }
        }

        return false;
      }
      return false;
    },
    isDraft(): boolean {
      if (this.diary) {
        return this.diary.status === "draft";
      }
      return false;
    },
    isApplied(): boolean {
      if (this.diary) {
        return this.diary.status === "applied";
      }
      return false;
    },
    cheifApproveStamp(): string {
      if (this.diary) {
        if (this.diary.approvedBy.chief && this.diary.approvedBy.chief != -1) {
          return `${this.diary.approvedBy.chief.lastName} ${this.diary.approvedBy.chief.firstName}`;
        }
      }
      return "-";
    },
    directorApproveStamp(): string {
      if (this.diary) {
        if (
          this.diary.approvedBy.director &&
          this.diary.approvedBy.director != -1
        ) {
          return `${this.diary.approvedBy.director.lastName} ${this.diary.approvedBy.director.firstName}`;
        }
      }
      return "-";
    },
    recorderStamp(): string {
      if (this.diary) {
        if (this.diary.createdBy) {
          return `${this.diary.createdBy.lastName} ${this.diary.createdBy.firstName}`;
        }
      }
      return this.$t('schoolDiary.draft');
    },
    appliedBy: {
      get() {
        return schoolDairyEditModule.appliedBy;
      },
      set(value: number | null) {
        schoolDairyEditModule.setAppliedBy(value);
      },
    },
    diarySetting: function (): NurseryDiarySettingResponse | null {
      return diarySettingModule.diarySetting;
    },
  },

  methods: {
    async saveAsDraft() {
      this.isUpdating = true;
      await schoolDairyEditModule.saveSchoolDiary("draft");
      this.isUpdating = false;
    },
    async saveAndApply() {
      this.isUpdating = true;
      await schoolDairyEditModule.saveSchoolDiary("applied");
      this.isUpdating = false;
    },
    async approve() {
      this.isUpdating = true;
      if (this.alertSetting.shouldAlert) {
        const answer = confirm(
          `${this.$t('schoolDiary.missingClassDiaries')}\n\n${this.alertSetting.classes.join(
            "\n"
          )}`
        );
        if (answer) {
          await schoolDairyEditModule.approveSchoolDiary();
        }
      } else {
        await schoolDairyEditModule.approveSchoolDiary();
      }
      this.isUpdating = false;
    },
    async reject() {
      this.isUpdating = true;
      await schoolDairyEditModule.rejectSchoolDiary();
      this.isUpdating = false;
    },
    getClassName(classId: number) {
      // return '' + this.classes.length;
      const find = this.classes.find((c) => c.classId === classId);
      if (find) {
        return find.className;
      }
      return "";
    },
    getCurrentClass(child: NurseryChildResponse) {
      // const currentYear = dayjs().year();
      if (this.diary && this.diary.date) {
        const currentYear = CommonUtil.getFiscalYear(this.diary.date);
        const find = child.nurseryClasses.find(
          (c) => String(c.year) == currentYear
        );
        if (find) {
          return find.className;
        }
        return "";
      }
    },
  },

  data: (): DataType => {
    return {
      isUpdating: false,
      loading: false,
      alertSetting: {
        shouldAlert: false,
        classes: [],
      },
    };
  },

  async mounted() {
    this.loading = true;

    const date = this.$route.params.date;
    if (date) {
      schoolDairyEditModule.setDate(date as string);
      const diaryResponse = await schoolDairyEditModule.getSchoolDiary();

      // alert setting
      if (diaryResponse && this.diarySetting) {
        this.diarySetting.schoolDiaryConfirmation.targetClasses.forEach((c) => {
          const isNotEmpty = diaryResponse.classDiaries.some(
            (d) => d.classId == c.classId
          );
          if (!isNotEmpty) {
            this.alertSetting.shouldAlert = true;
            this.alertSetting.classes.push(`${c.className}`);
          }
        });
      }
    }

    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      await classModule.listClass({
        nurseryId: nurseryId,
      });
      await nurseryUserModule.listStaffs({
        date: date as string,
        diaryId: this.diary?.schoolDiaryId,
        diaryType: 'school'
      });
      await diarySettingModule.listDiarySetting({ nurseryId });
    }

    this.loading = false;
  },
});
</script>
<style lang="scss" scoped>
.class-diary-content {
  font-size: 14px;
}

.class-diary-label {
  color: #8c8c8c;
  font-weight: bold;
  font-size: 14px;
}
</style>
